import React, { createContext, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Language = 'en' | 'fr'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Legacy translations object - now handled by i18next
// Kept for reference only
const legacyTranslations = {
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      candidates: 'Candidates',
      offers: 'Offers',
      aiAssistant: 'Copilot',
      analytics: 'Analytics',
      aiMatching: 'AI Matching',
      upload: 'Upload',
      settings: 'Settings'
    },

    // Login
    login: {
      welcome: 'Welcome Back',
      subtitle: 'Sign in to your Selektia account',
      title: 'Sign In',
      email: 'Email',
      emailPlaceholder: 'Enter your email',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      signIn: 'Sign In',
      signingIn: 'Signing In...',
      invalidCredentials: 'Invalid email or password',
      loginError: 'Login failed. Please try again.',
    },

    // AI Agent
    aiAssistant: {
      title: 'Copilot',
      subtitle: 'Get AI-powered insights and assistance for your recruitment process',
      quickActions: 'Quick Actions',
      chatTitle: 'Chat with AI',
      startConversation: 'Start a conversation with Copilot',
      inputPlaceholder: 'Ask me anything about recruitment...',
      thinking: 'AI is thinking...',
      analyzeCV: 'Analyze CV',
      analyzeCVDesc: 'Get detailed analysis of candidate resumes',
      analyzeCVPrompt: 'Please analyze this CV and provide insights about the candidate',
      analyzeCVResponse: 'I can help you analyze CVs. Please provide the CV content or upload the document, and I\'ll give you detailed insights about the candidate\'s qualifications, experience, and fit for specific roles.',
      createJobDesc: 'Create Job Description',
      createJobDescDesc: 'Generate compelling job descriptions',
      createJobDescPrompt: 'Help me create a job description for a new position',
      createJobDescResponse: 'I\'ll help you create a comprehensive job description. Please provide details about the role, required skills, experience level, and company culture, and I\'ll generate a professional job posting.',
      generateQuestions: 'Generate Interview Questions',
      generateQuestionsDesc: 'Create targeted interview questions',
      generateQuestionsPrompt: 'Generate interview questions for a candidate',
      generateQuestionsResponse: 'I can create tailored interview questions based on the role and candidate profile. Please specify the position, required skills, and interview type (technical, behavioral, etc.), and I\'ll generate relevant questions.',
      optimizeOffer: 'Optimize Job Offer',
      optimizeOfferDesc: 'Improve job offer attractiveness',
      optimizeOfferPrompt: 'Help me optimize this job offer to attract top talent',
      optimizeOfferResponse: 'I\'ll help you optimize your job offer. Please share the current offer details including salary, benefits, and role description, and I\'ll suggest improvements to make it more attractive to candidates.',
      defaultResponse: 'I\'m here to help with your recruitment needs. You can ask me to analyze CVs, create job descriptions, generate interview questions, optimize job offers, or any other recruitment-related tasks.'
    },

    // Analytics
    analytics: {
      title: 'Analytics',
      subtitle: 'Comprehensive insights into your recruitment performance',
      totalApplications: 'Total Applications',
      conversionRate: 'Conversion Rate',
      avgTimeToHire: 'Avg Time to Hire',
      aiInsights: 'AI Insights',
      applicationEvolution: 'Application Evolution',
      conversionFunnel: 'Conversion Funnel',
      topPerformingOffers: 'Top Performing Offers',
      sourcePerformance: 'Source Performance',
      recruitmentTimeAnalysis: 'Recruitment Time Analysis',
      applications: 'Applications',
      screening: 'Screening',
      interviews: 'Interviews',
      finalInterview: 'Final Interview',
      offers: 'Offers',
      hires: 'Hires'
    },

    // AI Matching
    aiMatching: {
      title: 'AI Matching',
      subtitle: 'Automatically match candidates to job offers using AI',
      minScore: 'Minimum Score',
      candidates: 'candidates',
      offers: 'offers',
      matching: 'Matching...',
      startMatching: 'Start Matching',
      groups: 'Groups',
      matchingResults: 'Matching Results',
      accept: 'Accept',
      reject: 'Reject',
      candidateProfile: 'Candidate Profile',
      experience: 'Experience',
      skills: 'Skills',
      offerDetails: 'Offer Details',
      matchingReasons: 'Matching Reasons',
      matchingSkills: 'Matching Skills',
      noResults: 'No matches found',
      noResultsDesc: 'Try adjusting the minimum score or run the matching process again',
      excellent: 'Excellent',
      veryGood: 'Very Good',
      good: 'Good',
      fair: 'Fair',
      createGroup: 'Create Group',
      candidatesInGroup: 'Candidates in Group',
      offersInGroup: 'Offers in Group'
    },

    // Candidate Groups
    candidateGroups: {
      title: 'Candidate Teams',
      subtitle: 'Organize candidates into teams and groups for better management',
      createTeam: 'Create Team',
      createNewTeam: 'Create New Team',
      teamName: 'Team Name',
      teamNamePlaceholder: 'Enter team name...',
      teamDescription: 'Team Description',
      teamDescriptionPlaceholder: 'Enter team description...',
      selectCandidates: 'Select Candidates',
      selectedCandidates: 'Selected Candidates',
      candidatesSelected: 'candidates selected',
      noCandidatesAvailable: 'No candidates available',
      cancel: 'Cancel',
      create: 'Create',
      searchCandidates: 'Search candidates...',
      searchGroups: 'Search groups...',
      candidatesFound: 'candidates found',
      members: 'members',
      addMember: 'Add Member',
      availableCandidates: 'Available Candidates'
    },
    
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome to Selektia! Here\'s what\'s happening with your recruitment pipeline.',
      stats: {
        totalCandidates: 'Total Candidates',
        activeOffers: 'Active Offers',
        hiresThisMonth: 'Hires This Month',
        conversionRate: 'Conversion Rate'
      },
      pipeline: {
        title: 'Candidate Pipeline'
      },
      activity: {
        title: 'Recent Activity'
      }
    },
    
    // Candidates
            candidates: {
              title: 'Candidates',
              subtitle: 'Manage your candidate pipeline and track application progress.',
              addCandidate: 'Add Candidate',
              stats: {
                totalCandidates: 'Total Candidates',
                hiredCandidates: 'Hired Candidates',
                newCandidates7Days: 'New (7 days)',
                pendingCVs: 'CVs Pending'
              },
              createNewCandidate: 'Create New Candidate',
              cvUpload: 'Upload CV',
              cvUploadPlaceholder: 'Click to upload CV or drag and drop',
              cvUploadSupported: 'Supported formats: PDF, DOC, DOCX, TXT',
              selectGroup: 'Select Team',
              noGroup: 'No team selected',
              selectGroupHelp: 'Optional: Assign candidate to a team',
              manualFields: 'Manual Fields (Optional)',
              manualFieldsHelp: 'These fields will be auto-filled from the CV, but you can override them if needed',
              search: 'Search candidates...',
      cardView: 'Card View',
      listView: 'List View',
      name: 'Name',
      namePlaceholder: 'Enter candidate name',
      email: 'Email',
      emailPlaceholder: 'Enter email address',
      phone: 'Phone',
      phonePlaceholder: 'Enter phone number',
      role: 'Role',
      rolePlaceholder: 'Enter job role',
      experience: 'Experience',
      experiencePlaceholder: 'e.g., 3 years',
      location: 'Location',
      locationPlaceholder: 'Enter location',
      skills: 'Skills',
      skillsPlaceholder: 'React, TypeScript, Node.js',
      skillsHelp: 'Separate skills with commas',
      bio: 'Bio',
      bioPlaceholder: 'Enter candidate bio',
      cancel: 'Cancel',
      create: 'Create',
      status: {
        all: 'All Statuses',
        applied: 'Applied',
        interview: 'Interview',
        offer: 'Offer',
        hired: 'Hired',
        rejected: 'Rejected'
      },
      roles: {
        all: 'All Roles'
      },
      group: {
        all: 'All Groups',
        ungrouped: 'Ungrouped'
      },
      filters: 'Filters',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      noResults: 'No candidates found matching your filters.',
      noData: 'No candidates found. Add your first candidate to get started.'
    },
    
    // Offers
    'offers.title': 'Offers',
    'offers.subtitle': 'Manage job offers and track their status throughout the hiring process.',
    'offers.createOffer': 'Create Offer',
    'offers.search': 'Search offers...',
    'offers.status.all': 'All Statuses',
    'offers.status.pending': 'Pending',
    'offers.status.accepted': 'Accepted',
    'offers.status.declined': 'Declined',
    'offers.total': 'Total',
    'offers.filters': 'Filters',
    'offers.allOffers': 'All Offers',
    'offers.noResults': 'No offers found matching your filters.',
    'offers.noData': 'No offers found. Create your first offer to get started.',
    
    // Upload
    'upload.title': 'Resume Upload',
    'upload.subtitle': 'Upload and manage candidate resumes and documents.',
    'upload.selectCandidate': 'Select Candidate',
    'upload.chooseCandidate': 'Choose a candidate...',
    'upload.dragDrop': 'Drag & drop files here',
    'upload.clickToBrowse': 'or click to browse files',
    'upload.supportedFormats': 'Supports PDF, DOC, DOCX, TXT files',
    'upload.dropFiles': 'Drop the files here...',
    'upload.selectCandidateFirst': 'Select a candidate first',
    'upload.uploading': 'Uploading...',
    'upload.history': 'Upload History',
    'upload.noFiles': 'No files uploaded yet',
    'upload.uploadResume': 'Upload Resume',
    'upload.guidelines': 'Upload Guidelines',
    'upload.supportedFormats.title': 'Supported Formats',
    'upload.bestPractices.title': 'Best Practices',
    'upload.bestPractices.filename': 'Use clear, descriptive filenames',
    'upload.bestPractices.size': 'Keep file sizes under 10MB',
    'upload.bestPractices.readable': 'Ensure documents are readable',
    
    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your application preferences and system settings.',
    'settings.appearance': 'Appearance',
    'settings.theme': 'Theme',
    'settings.themeDescription': 'Choose your preferred color scheme',
    'settings.light': 'Light',
    'settings.dark': 'Dark',
    'settings.notifications': 'Notifications',
    'settings.emailNotifications': 'Email Notifications',
    'settings.emailDescription': 'Receive email updates for candidate activities',
    'settings.pushNotifications': 'Push Notifications',
    'settings.pushDescription': 'Get real-time updates in your browser',
    'settings.weeklyReports': 'Weekly Reports',
    'settings.weeklyDescription': 'Receive weekly summary reports',
    'settings.dataExport': 'Data Export',
    'settings.exportCandidates': 'Export Candidates',
    'settings.exportCandidatesDesc': 'Download all candidate data as CSV',
    'settings.exportOffers': 'Export Offers',
    'settings.exportOffersDesc': 'Download all offer data as CSV',
    'settings.security': 'Security',
    'settings.twoFactor': 'Two-Factor Authentication',
    'settings.twoFactorDesc': 'Add an extra layer of security to your account',
    'settings.enable': 'Enable',
    'settings.sessionTimeout': 'Session Timeout',
    'settings.sessionDescription': 'Automatically log out after inactivity',
    'settings.systemInfo': 'System Information',
    'settings.version': 'Version',
    'settings.lastUpdated': 'Last Updated',
    'settings.database': 'Database',
    'settings.environment': 'Environment',
    'settings.advanced': 'Advanced Settings',
    'settings.debugMode': 'Debug Mode',
    'settings.debugDescription': 'Enable detailed logging and debugging',
    'settings.autoSave': 'Auto-save',
    'settings.autoSaveDescription': 'Automatically save changes as you type',
    
    // Candidate Detail
    candidateDetail: {
      back: 'Back',
      backToCandidates: 'Back to Candidates',
      candidateInformation: 'Candidate Information',
      edit: 'Edit',
      experience: 'Experience',
      status: 'Status',
      lastContact: 'Last Contact',
      bio: 'Bio',
      skills: 'Skills',
      applied: 'Applied',
      resumeDocuments: 'Resume & Documents',
      uploaded: 'Uploaded',
      view: 'View',
      download: 'Download',
      noDocumentsUploaded: 'No documents uploaded yet',
      uploadResume: 'Upload Resume',
      statusManagement: 'Status Management',
      moveTo: 'Move to',
      aiMatches: 'AI Matches',
      viewOffer: 'View Offer',
      quickActions: 'Quick Actions',
      sendEmail: 'Send Email',
      scheduleCall: 'Schedule Call',
      scheduleInterview: 'Schedule Interview',
      candidateNotFound: 'Candidate not found',
      loadingPdf: 'Loading PDF...',
      retry: 'Retry',
      authenticationError: 'If this is an authentication error, please try refreshing the page and logging in again.',
      pdf: 'PDF',
      noAiMatches: 'No AI matches found',
      noAiMatchesDescription: 'Run AI matching to find suitable job offers for this candidate'
    },
    
    // Common
    'common.export': 'Export',
    'common.download': 'Download',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.reset': 'Reset',
    'common.apply': 'Apply',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.submit': 'Submit',
    'common.upload': 'Upload',
    'common.select': 'Select',
    'common.choose': 'Choose',
    'common.all': 'All',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.or': 'or',
    'common.and': 'and',
    'common.with': 'with',
    'common.without': 'without',
    'common.from': 'from',
    'common.to': 'to',
    'common.at': 'at',
    'common.on': 'on',
    'common.in': 'in',
    'common.of': 'of',
    'common.for': 'for',
    'common.by': 'by',
    'common.about': 'about',
    'common.more': 'more',
    'common.less': 'less',
    'common.most': 'most',
    'common.least': 'least',
    'common.best': 'best',
    'common.worst': 'worst',
    'common.better': 'better',
    'common.worse': 'worse',
    'common.good': 'good',
    'common.bad': 'bad',
    'common.new': 'new',
    'common.old': 'old',
    'common.recent': 'recent',
    'common.updated': 'updated',
    'common.created': 'created',
    'common.modified': 'modified',
    'common.deleted': 'deleted',
    'common.active': 'active',
    'common.inactive': 'inactive',
    'common.enabled': 'enabled',
    'common.disabled': 'disabled',
    'common.visible': 'visible',
    'common.hidden': 'hidden',
    'common.required': 'required',
    'common.optional': 'optional',
    'common.available': 'available',
    'common.unavailable': 'unavailable',
    'common.online': 'online',
    'common.offline': 'offline',
    'common.public': 'public',
    'common.private': 'private',
    'common.shared': 'shared',
    'common.personal': 'personal',
    'common.global': 'global',
    'common.local': 'local',
    'common.remote': 'remote',
    'common.external': 'external',
    'common.internal': 'internal',
    'common.primary': 'primary',
    'common.secondary': 'secondary',
    'common.tertiary': 'tertiary',
    'common.main': 'main',
    'common.sub': 'sub',
    'common.major': 'major',
    'common.minor': 'minor',
    'common.critical': 'critical',
    'common.normal': 'normal',
    'common.high': 'high',
    'common.low': 'low',
    'common.medium': 'medium',
    'common.large': 'large',
    'common.small': 'small',
    'common.big': 'big',
    'common.tiny': 'tiny',
    'common.huge': 'huge',
    'common.massive': 'massive',
    'common.mini': 'mini',
    'common.micro': 'micro',
    'common.macro': 'macro',
    'common.mega': 'mega',
    'common.giga': 'giga',
    'common.tera': 'tera',
    'common.peta': 'peta',
    'common.exa': 'exa',
    'common.zetta': 'zetta',
    'common.yotta': 'yotta'
  },
  fr: {
    // Navigation
    nav: {
      dashboard: 'Tableau de bord',
      candidates: 'Candidats',
      offers: 'Offres d\'emploi',
      aiAssistant: 'Copilot',
      analytics: 'Analyses',
      aiMatching: 'Correspondance IA',
      upload: 'Télécharger',
      settings: 'Paramètres'
    },

    // Login
    login: {
      welcome: 'Bon retour',
      subtitle: 'Connectez-vous à votre compte Selektia',
      title: 'Se connecter',
      email: 'E-mail',
      emailPlaceholder: 'Entrez votre e-mail',
      password: 'Mot de passe',
      passwordPlaceholder: 'Entrez votre mot de passe',
      signIn: 'Se connecter',
      signingIn: 'Connexion...',
      invalidCredentials: 'E-mail ou mot de passe invalide',
      loginError: 'Échec de la connexion. Veuillez réessayer.',
      demoCredentials: 'Identifiants de démonstration',
      use: 'Utiliser'
    },

    // AI Agent
    aiAssistant: {
      title: 'Copilot',
      subtitle: 'Obtenez des informations et une assistance alimentées par l\'IA pour votre processus de recrutement',
      quickActions: 'Actions Rapides',
      chatTitle: 'Chat avec l\'IA',
      startConversation: 'Commencez une conversation avec le Copilot',
      inputPlaceholder: 'Demandez-moi n\'importe quoi sur le recrutement...',
      thinking: 'L\'IA réfléchit...',
      analyzeCV: 'Analyser un CV',
      analyzeCVDesc: 'Obtenez une analyse détaillée des CV des candidats',
      analyzeCVPrompt: 'Veuillez analyser ce CV et fournir des informations sur le candidat',
      analyzeCVResponse: 'Je peux vous aider à analyser les CV. Veuillez fournir le contenu du CV ou télécharger le document, et je vous donnerai des informations détaillées sur les qualifications, l\'expérience et l\'adéquation du candidat pour des rôles spécifiques.',
      createJobDesc: 'Créer une description de poste',
      createJobDescDesc: 'Générer des descriptions de poste convaincantes',
      createJobDescPrompt: 'Aidez-moi à créer une description de poste pour un nouveau poste',
      createJobDescResponse: 'Je vais vous aider à créer une description de poste complète. Veuillez fournir des détails sur le rôle, les compétences requises, le niveau d\'expérience et la culture d\'entreprise, et je générerai une offre d\'emploi professionnelle.',
      generateQuestions: 'Générer des questions d\'entretien',
      generateQuestionsDesc: 'Créer des questions d\'entretien ciblées',
      generateQuestionsPrompt: 'Générer des questions d\'entretien pour un candidat',
      generateQuestionsResponse: 'Je peux créer des questions d\'entretien adaptées basées sur le rôle et le profil du candidat. Veuillez spécifier le poste, les compétences requises et le type d\'entretien (technique, comportemental, etc.), et je générerai des questions pertinentes.',
      optimizeOffer: 'Optimiser une offre d\'emploi',
      optimizeOfferDesc: 'Améliorer l\'attractivité des offres d\'emploi',
      optimizeOfferPrompt: 'Aidez-moi à optimiser cette offre d\'emploi pour attirer les meilleurs talents',
      optimizeOfferResponse: 'Je vais vous aider à optimiser votre offre d\'emploi. Veuillez partager les détails de l\'offre actuelle, y compris le salaire, les avantages et la description du rôle, et je suggérerai des améliorations pour la rendre plus attractive aux candidats.',
      defaultResponse: 'Je suis là pour vous aider avec vos besoins de recrutement. Vous pouvez me demander d\'analyser des CV, créer des descriptions de poste, générer des questions d\'entretien, optimiser des offres d\'emploi ou toute autre tâche liée au recrutement.'
    },

    // Analytics
    analytics: {
      title: 'Analyses',
      subtitle: 'Informations complètes sur les performances de votre recrutement',
      totalApplications: 'Total des candidatures',
      conversionRate: 'Taux de conversion',
      avgTimeToHire: 'Temps moyen d\'embauche',
      aiInsights: 'Insights IA',
      applicationEvolution: 'Évolution des candidatures',
      conversionFunnel: 'Entonnoir de conversion',
      topPerformingOffers: 'Offres les plus performantes',
      sourcePerformance: 'Performance par source',
      recruitmentTimeAnalysis: 'Analyse du temps de recrutement',
      applications: 'Candidatures',
      screening: 'Présélection',
      interviews: 'Entretiens',
      finalInterview: 'Entretien final',
      offers: 'Offres',
      hires: 'Embauches'
    },

    // AI Matching
    aiMatching: {
      title: 'Correspondance IA',
      subtitle: 'Associez automatiquement les candidats aux offres d\'emploi en utilisant l\'IA',
      minScore: 'Score minimum',
      candidates: 'Candidats',
      offers: 'offres',
      matching: 'Correspondance...',
      startMatching: 'Commencer la correspondance',
      groups: 'Groupes',
      matchingResults: 'Résultats de correspondance',
      accept: 'Accepter',
      reject: 'Rejeter',
      candidateProfile: 'Profil du candidat',
      experience: 'Expérience',
      skills: 'Compétences',
      offerDetails: 'Détails de l\'offre',
      matchingReasons: 'Raisons de correspondance',
      matchingSkills: 'Compétences correspondantes',
      noResults: 'Aucune correspondance trouvée',
      noResultsDesc: 'Essayez d\'ajuster le score minimum ou relancez le processus de correspondance',
      excellent: 'Excellent',
      veryGood: 'Très bon',
      good: 'Bon',
      fair: 'Correct',
      createGroup: 'Créer un groupe',
      candidatesInGroup: 'Candidats dans le groupe',
      offersInGroup: 'Offres dans le groupe'
    },

    // Candidate Groups
    candidateGroups: {
      title: 'Équipes de Candidats',
      subtitle: 'Organisez les candidats en équipes et groupes pour une meilleure gestion',
      createTeam: 'Créer une Équipe',
      createNewTeam: 'Créer une Nouvelle Équipe',
      teamName: 'Nom de l\'Équipe',
      teamNamePlaceholder: 'Entrez le nom de l\'équipe...',
      teamDescription: 'Description de l\'Équipe',
      teamDescriptionPlaceholder: 'Entrez la description de l\'équipe...',
      selectCandidates: 'Sélectionner des Candidats',
      selectedCandidates: 'Candidats Sélectionnés',
      candidatesSelected: 'Candidats sélectionnés',
      noCandidatesAvailable: 'Aucun candidat disponible',
      cancel: 'Annuler',
      create: 'Créer',
      searchCandidates: 'Rechercher des candidats...',
      candidatesFound: 'Candidats trouvés',
      members: 'membres',
      addMember: 'Ajouter un Membre',
      availableCandidates: 'Candidats Disponibles'
    },
    
    // Dashboard
    dashboard: {
      title: 'Tableau de bord',
      welcome: 'Bienvenue dans Selektia ! Voici ce qui se passe dans votre pipeline de recrutement.',
      stats: {
        totalCandidates: 'Total des Candidats',
        activeOffers: 'Offres actives',
        hiresThisMonth: 'Embauches ce mois',
        conversionRate: 'Taux de conversion'
      },
      pipeline: {
        title: 'Pipeline des Candidats'
      },
      activity: {
        title: 'Activité récente'
      }
    },
    
    // Candidates
    candidates: {
      title: 'Candidats',
      subtitle: 'Gérez votre pipeline de Candidats et suivez les progrès des candidatures.',
      addCandidate: 'Ajouter un candidat',
      stats: {
        totalCandidates: 'Total Candidats',
        hiredCandidates: 'Candidats Embauchés',
        newCandidates7Days: 'Nouveaux (7j)',
        pendingCVs: 'CVs En Attente'
      },
      createNewCandidate: 'Créer un Nouveau Candidat',
      cvUpload: 'Télécharger CV',
      cvUploadPlaceholder: 'Cliquez pour télécharger le CV ou glissez-déposez',
      cvUploadSupported: 'Formats supportés: PDF, DOC, DOCX, TXT',
      selectGroup: 'Sélectionner une Équipe',
      noGroup: 'Aucune équipe sélectionnée',
      selectGroupHelp: 'Optionnel: Assigner le candidat à une équipe',
      manualFields: 'Champs Manuels (Optionnel)',
      manualFieldsHelp: 'Ces champs seront remplis automatiquement depuis le CV, mais vous pouvez les modifier si nécessaire',
      search: 'Rechercher des candidats...',
      cardView: 'Vue Cartes',
      listView: 'Vue Liste',
      name: 'Nom',
      namePlaceholder: 'Entrez le nom du candidat',
      email: 'Email',
      emailPlaceholder: 'Entrez l\'adresse email',
      phone: 'Téléphone',
      phonePlaceholder: 'Entrez le numéro de téléphone',
      role: 'Rôle',
      rolePlaceholder: 'Entrez le poste',
      experience: 'Expérience',
      experiencePlaceholder: 'ex: 3 ans',
      location: 'Localisation',
      locationPlaceholder: 'Entrez la localisation',
      skills: 'Compétences',
      skillsPlaceholder: 'React, TypeScript, Node.js',
      skillsHelp: 'Séparez les compétences par des virgules',
      bio: 'Biographie',
      bioPlaceholder: 'Entrez la biographie du candidat',
      cancel: 'Annuler',
      create: 'Créer',
      status: {
        all: 'Tous les statuts',
        applied: 'Candidature',
        interview: 'Entretien',
        offer: 'Offre',
        hired: 'Embauché',
        rejected: 'Rejeté'
      },
      roles: {
        all: 'Tous les rôles'
      },
      group: {
        all: 'Tous les Groupes',
        ungrouped: 'Non Groupés'
      },
      filters: 'Filtres',
      view: 'Voir',
      edit: 'Modifier',
      delete: 'Supprimer',
      noResults: 'Aucun candidat trouvé correspondant à vos filtres.',
      noData: 'Aucun candidat trouvé. Ajoutez votre premier candidat pour commencer.'
    },
    
    // Offers
    'offers.title': 'Offres',
    'offers.subtitle': 'Gérez les offres d\'emploi et suivez leur statut tout au long du processus d\'embauche.',
    'offers.createOffer': 'Créer une offre',
    'offers.search': 'Rechercher des offres...',
    'offers.status.all': 'Tous les statuts',
    'offers.status.pending': 'En attente',
    'offers.status.accepted': 'Accepté',
    'offers.status.declined': 'Refusé',
    'offers.total': 'Total',
    'offers.filters': 'Filtres',
    'offers.allOffers': 'Toutes les offres',
    'offers.noResults': 'Aucune offre trouvée correspondant à vos filtres.',
    'offers.noData': 'Aucune offre trouvée. Créez votre première offre pour commencer.',
    
    // Upload
    'upload.title': 'Téléchargement de CV',
    'upload.subtitle': 'Téléchargez et gérez les CV et documents des Candidats.',
    'upload.selectCandidate': 'Sélectionner un candidat',
    'upload.chooseCandidate': 'Choisir un candidat...',
    'upload.dragDrop': 'Glissez-déposez les fichiers ici',
    'upload.clickToBrowse': 'ou cliquez pour parcourir les fichiers',
    'upload.supportedFormats': 'Supporte les formats PDF, DOC, DOCX, TXT',
    'upload.dropFiles': 'Déposez les fichiers ici...',
    'upload.selectCandidateFirst': 'Sélectionnez d\'abord un candidat',
    'upload.uploading': 'Téléchargement...',
    'upload.history': 'Historique des téléchargements',
    'upload.noFiles': 'Aucun fichier téléchargé pour le moment',
    'upload.uploadResume': 'Télécharger un CV',
    'upload.guidelines': 'Directives de téléchargement',
    'upload.supportedFormats.title': 'Formats supportés',
    'upload.bestPractices.title': 'Meilleures pratiques',
    'upload.bestPractices.filename': 'Utilisez des noms de fichiers clairs et descriptifs',
    'upload.bestPractices.size': 'Gardez les tailles de fichiers sous 10 Mo',
    'upload.bestPractices.readable': 'Assurez-vous que les documents sont lisibles',
    
    // Settings
    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérez vos préférences d\'application et paramètres système.',
    'settings.appearance': 'Apparence',
    'settings.theme': 'Thème',
    'settings.themeDescription': 'Choisissez votre schéma de couleurs préféré',
    'settings.light': 'Clair',
    'settings.dark': 'Sombre',
    'settings.notifications': 'Notifications',
    'settings.emailNotifications': 'Notifications par e-mail',
    'settings.emailDescription': 'Recevez des mises à jour par e-mail pour les activités des candidats',
    'settings.pushNotifications': 'Notifications push',
    'settings.pushDescription': 'Recevez des mises à jour en temps réel dans votre navigateur',
    'settings.weeklyReports': 'Rapports hebdomadaires',
    'settings.weeklyDescription': 'Recevez des rapports de résumé hebdomadaires',
    'settings.dataExport': 'Exportation de données',
    'settings.exportCandidates': 'Exporter les Candidats',
    'settings.exportCandidatesDesc': 'Télécharger toutes les données des Candidats en CSV',
    'settings.exportOffers': 'Exporter les offres',
    'settings.exportOffersDesc': 'Télécharger toutes les données des offres en CSV',
    'settings.security': 'Sécurité',
    'settings.twoFactor': 'Authentification à deux facteurs',
    'settings.twoFactorDesc': 'Ajoutez une couche de sécurité supplémentaire à votre compte',
    'settings.enable': 'Activer',
    'settings.sessionTimeout': 'Délai d\'expiration de session',
    'settings.sessionDescription': 'Se déconnecter automatiquement après inactivité',
    'settings.systemInfo': 'Informations système',
    'settings.version': 'Version',
    'settings.lastUpdated': 'Dernière mise à jour',
    'settings.database': 'Base de données',
    'settings.environment': 'Environnement',
    'settings.advanced': 'Paramètres avancés',
    'settings.debugMode': 'Mode debug',
    'settings.debugDescription': 'Activer la journalisation détaillée et le débogage',
    'settings.autoSave': 'Sauvegarde automatique',
    'settings.autoSaveDescription': 'Sauvegarder automatiquement les modifications pendant la frappe',
    
    // Candidate Detail
    candidateDetail: {
      back: 'Retour',
      backToCandidates: 'Retour aux Candidats',
      candidateInformation: 'Informations du Candidat',
      edit: 'Modifier',
      experience: 'Expérience',
      status: 'Statut',
      lastContact: 'Dernier Contact',
      bio: 'Biographie',
      skills: 'Compétences',
      applied: 'Candidaté',
      resumeDocuments: 'CV et Documents',
      uploaded: 'Téléchargé',
      view: 'Voir',
      download: 'Télécharger',
      noDocumentsUploaded: 'Aucun document téléchargé',
      uploadResume: 'Télécharger le CV',
      statusManagement: 'Gestion du Statut',
      moveTo: 'Déplacer vers',
      aiMatches: 'Correspondances IA',
      viewOffer: 'Voir l\'Offre',
      quickActions: 'Actions Rapides',
      sendEmail: 'Envoyer un Email',
      scheduleCall: 'Planifier un Appel',
      scheduleInterview: 'Planifier un Entretien',
      candidateNotFound: 'Candidat non trouvé',
      loadingPdf: 'Chargement du PDF...',
      retry: 'Réessayer',
      authenticationError: 'Si c\'est une erreur d\'authentification, veuillez actualiser la page et vous reconnecter.',
      pdf: 'PDF',
      noAiMatches: 'Aucune correspondance IA trouvée',
      noAiMatchesDescription: 'Lancez la correspondance IA pour trouver des offres d\'emploi adaptées à ce candidat'
    },
    
    // Common
    'common.export': 'Exporter',
    'common.download': 'Télécharger',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.view': 'Voir',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.confirm': 'Confirmer',
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.warning': 'Avertissement',
    'common.info': 'Information',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.clear': 'Effacer',
    'common.reset': 'Réinitialiser',
    'common.apply': 'Appliquer',
    'common.close': 'Fermer',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.submit': 'Soumettre',
    'common.upload': 'Télécharger',
    'common.select': 'Sélectionner',
    'common.choose': 'Choisir',
    'common.all': 'Tout',
    'common.none': 'Aucun',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.or': 'ou',
    'common.and': 'et',
    'common.with': 'avec',
    'common.without': 'sans',
    'common.from': 'de',
    'common.to': 'à',
    'common.at': 'à',
    'common.on': 'sur',
    'common.in': 'dans',
    'common.of': 'de',
    'common.for': 'pour',
    'common.by': 'par',
    'common.about': 'à propos',
    'common.more': 'plus',
    'common.less': 'moins',
    'common.most': 'le plus',
    'common.least': 'le moins',
    'common.best': 'meilleur',
    'common.worst': 'pire',
    'common.better': 'mieux',
    'common.worse': 'pire',
    'common.good': 'bon',
    'common.bad': 'mauvais',
    'common.new': 'nouveau',
    'common.old': 'ancien',
    'common.recent': 'récent',
    'common.updated': 'mis à jour',
    'common.created': 'créé',
    'common.modified': 'modifié',
    'common.deleted': 'supprimé',
    'common.active': 'actif',
    'common.inactive': 'inactif',
    'common.enabled': 'activé',
    'common.disabled': 'désactivé',
    'common.visible': 'visible',
    'common.hidden': 'caché',
    'common.required': 'requis',
    'common.optional': 'optionnel',
    'common.available': 'disponible',
    'common.unavailable': 'indisponible',
    'common.online': 'en ligne',
    'common.offline': 'hors ligne',
    'common.public': 'public',
    'common.private': 'privé',
    'common.shared': 'partagé',
    'common.personal': 'personnel',
    'common.global': 'global',
    'common.local': 'local',
    'common.remote': 'distant',
    'common.external': 'externe',
    'common.internal': 'interne',
    'common.primary': 'primaire',
    'common.secondary': 'secondaire',
    'common.tertiary': 'tertiaire',
    'common.main': 'principal',
    'common.sub': 'sous',
    'common.major': 'majeur',
    'common.minor': 'mineur',
    'common.critical': 'critique',
    'common.normal': 'normal',
    'common.high': 'élevé',
    'common.low': 'faible',
    'common.medium': 'moyen',
    'common.large': 'grand',
    'common.small': 'petit',
    'common.big': 'gros',
    'common.tiny': 'minuscule',
    'common.huge': 'énorme',
    'common.massive': 'massif',
    'common.mini': 'mini',
    'common.micro': 'micro',
    'common.macro': 'macro',
    'common.mega': 'méga',
    'common.giga': 'giga',
    'common.tera': 'téra',
    'common.peta': 'péta',
    'common.exa': 'exa',
    'common.zetta': 'zetta',
    'common.yotta': 'yotta'
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { t: i18nT, i18n } = useTranslation()

  const language = (i18n.language || 'en') as Language

  const setLanguage = (lang: Language) => {
    i18n.changeLanguage(lang)
  }

  const t = (key: string): string => {
    return i18nT(key)
  }

  // Keep <html lang> in sync so native browser validation messages localize
  useEffect(() => {
    const langCode = (i18n.language || 'en').split('-')[0]
    if (typeof document !== 'undefined') {
      document.documentElement.lang = langCode
    }
  }, [i18n.language])

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
